// Ablesungen – reine Funktionen. ablesungen.verbrauch ist gespeichert (Stand minus vorheriger Stand); wird eine
// Ablesung gelöscht oder davor nachgetragen, muss der Verbrauch der direkt folgenden Ablesung neu berechnet werden.

export interface AblesungKern {
  id: string;
  datum: string;
  wert: number;
  verbrauch: number | null;
  /** Reihenfolge bei gleichem Datum */
  created_at?: string | null;
}

/** Älteste zuerst; gleiches Datum nach Erfassung. */
export function chronologisch<T extends AblesungKern>(ablesungen: readonly T[]): T[] {
  return [...ablesungen].sort((a, b) => a.datum.localeCompare(b.datum) || (a.created_at ?? '').localeCompare(b.created_at ?? ''));
}

const runde = (n: number) => Math.round(n * 1000) / 1000;

export interface VerbrauchKorrektur { id: string; verbrauch: number | null }

/** Wird die Ablesung `id` gelöscht: neuer Verbrauch der folgenden Ablesung (gegen die davor), sonst null. */
export function korrekturNachLoeschen(ablesungen: readonly AblesungKern[], id: string): VerbrauchKorrektur | null {
  const s = chronologisch(ablesungen);
  const i = s.findIndex(a => a.id === id);
  const folge = i >= 0 ? s[i + 1] : undefined;
  if (!folge) return null;
  const vorher = i > 0 ? s[i - 1] : undefined;
  const verbrauch = vorher ? runde(folge.wert - vorher.wert) : null;
  return verbrauch === folge.verbrauch ? null : { id: folge.id, verbrauch };
}

/**
 * Nach dem Einfügen einer Ablesung (`neu`, schon in der Liste enthalten): neuer Verbrauch der direkt folgenden
 * Ablesung, falls die neue dazwischen liegt (Nachtrag). Liegt sie am Ende, ist nichts zu tun.
 */
export function korrekturNachEinfuegen(ablesungen: readonly AblesungKern[], neuId: string): VerbrauchKorrektur | null {
  const s = chronologisch(ablesungen);
  const i = s.findIndex(a => a.id === neuId);
  const neu = s[i];
  const folge = i >= 0 ? s[i + 1] : undefined;
  if (!neu || !folge) return null;
  const verbrauch = runde(folge.wert - neu.wert);
  return verbrauch === folge.verbrauch ? null : { id: folge.id, verbrauch };
}

/** Pfad im Bucket aus der gespeicherten öffentlichen URL („…/object/public/zaehler-fotos/<pfad>“). */
export function fotoPfad(url: string | null | undefined, bucket = 'zaehler-fotos'): string | null {
  if (!url) return null;
  const marke = `/object/public/${bucket}/`;
  const i = url.indexOf(marke);
  if (i < 0) return null;
  const pfad = url.slice(i + marke.length).split(/[?#]/)[0] ?? '';
  try { return decodeURIComponent(pfad) || null; } catch { return pfad || null; }
}

/** Ø-Verbrauch pro Monat wie im Immobilienbereich (erste bis letzte Ablesung, 30 Tage); null unter 2 Ablesungen oder ≤ 0. */
export { verbrauchProMonat } from '../immobilien/zaehlerFormular';

/** „171,4 kWh“ – eine Nachkommastelle */
export function durchschnittText(proMonat: number, einheit: string | null | undefined): string {
  return `${proMonat.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${einheit ?? ''}`.trim();
}

/** Verbrauchs-Pille in der Ablesungsliste: „+1.234“ (ohne Einheit), negativ rot. */
export function verbrauchPille(verbrauch: number | null): { text: string; negativ: boolean } | null {
  if (verbrauch === null || Number.isNaN(verbrauch)) return null;
  return { text: `${verbrauch > 0 ? '+' : ''}${verbrauch.toLocaleString('de-DE')}`, negativ: verbrauch < 0 };
}

export interface Balken { id: string; datum: string; verbrauch: number; anteil: number }

/** Verbrauchsverlauf: letzte 12 Ablesungen mit Verbrauch ≥ 0, chronologisch, Anteil am Maximum (0–1). */
export function verlaufBalken(ablesungen: readonly AblesungKern[]): Balken[] {
  const mit = chronologisch(ablesungen).filter((a): a is AblesungKern & { verbrauch: number } => a.verbrauch !== null && a.verbrauch >= 0).slice(-12);
  const max = Math.max(...mit.map(a => a.verbrauch), 1);
  return mit.map(a => ({ id: a.id, datum: a.datum, verbrauch: a.verbrauch, anteil: a.verbrauch / max }));
}

/** „Sep“ – Monat kurz ohne Punkt */
export function monatKurz(iso: string): string {
  const [j, m] = iso.split('-').map(Number);
  if (!j || !m) return '';
  return new Date(Date.UTC(j, m - 1, 15)).toLocaleDateString('de-DE', { month: 'short', timeZone: 'UTC' }).replace('.', '');
}

/** #512: Dateiendung für den Foto-Pfad (nur einfache Bild-Endungen, sonst jpg). */
export function fotoEndung(dateiname: string): string {
  const e = (dateiname.split('.').pop() ?? '').toLowerCase();
  return /^(jpe?g|png|webp|heic|heif|gif)$/.test(e) ? e : 'jpg';
}
